import React, { useEffect, useState } from "react";
import { useAuth, api } from "../../lib/auth";
import { RefreshCw, Upload, Trash2, Sparkles, MapPin, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useActivation } from "../../hooks/useActivation";
import { EmailPreferencesSection } from "../../components/portal/EmailPreferencesSection";

/**
 * Small in-Profile Settings section.
 *
 *  - Replay Northstar Introduction: only visible once activated. Sets
 *    `awakening_replayed_at` and navigates to /northstar/welcome.
 *    Guaranteed not to alter activation, connection, or intake state
 *    (server-side hardened).
 *  - Profile photo: real AWS S3 upload via /api/northstar/profile/photo.
 *    Falls back gracefully to the initial-letter avatar if S3 is
 *    unavailable.
 */
export default function Profile() {
  const { user } = useAuth();
  const [photo, setPhoto] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [replaying, setReplaying] = useState(false);
  const navigate = useNavigate();
  const { data: activation } = useActivation();

  React.useEffect(() => {
    api.get("/northstar/profile").then((r) => setPhoto(r.data)).catch(() => setPhoto(null));
  }, []);

  const onPhotoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/northstar/profile/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhoto((p) => ({ ...(p || {}), photo_url: r.data.url }));
      toast.success("Photo updated.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed. Please try again.";
      toast.error(detail);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onPhotoRemove = async () => {
    try {
      await api.delete("/northstar/profile/photo");
      setPhoto((p) => ({ ...(p || {}), photo_url: null }));
      toast.success("Photo removed.");
    } catch (err) {
      toast.error("Could not remove photo.");
    }
  };

  const onReplay = async () => {
    if (activation?.status !== "activated") return;
    setReplaying(true);
    try {
      await api.post("/northstar/awakening/replay");
      navigate("/northstar/welcome");
    } catch (err) {
      toast.error("Could not replay right now.");
      setReplaying(false);
    }
  };

  const hasPhoto = !!photo?.photo_url;
  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-page">
      <h1 className="text-3xl font-extrabold tracking-tight text-[#0B132B]">Profile</h1>

      {/* Identity + photo */}
      <div className="bg-white rounded-2xl border border-gray-100 p-8">
        <div className="flex items-center gap-5 mb-6">
          <div
            className="w-20 h-20 rounded-full overflow-hidden bg-[#1A5FAB] text-white flex items-center justify-center font-bold text-2xl"
            data-testid={hasPhoto ? "profile-photo-preview" : "profile-initial-avatar"}
          >
            {hasPhoto ? (
              <img
                src={photo.photo_url}
                alt="Profile"
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              initial
            )}
          </div>
          <div>
            <div className="font-bold text-[#0B132B] text-xl">{user.name}</div>
            <div className="text-sm text-[#4B5563]">{user.email}</div>
            <div className="text-xs text-[#23803F] font-semibold uppercase mt-1">{user.role}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {/* Phase 11 — Mobile: split into "Take Photo" (camera capture) and
              "Choose from library" (gallery). On desktop the camera control
              is still shown; browsers without a camera will fall through to
              the file picker. This preserves gallery upload as required. */}
          <label
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0B132B] text-white text-sm font-semibold cursor-pointer hover:bg-[#1a2a4d] transition-colors sm:hidden"
            data-testid="profile-photo-camera-label"
          >
            <Upload className="w-4 h-4" />
            {uploading ? "Uploading…" : "Take Photo"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="user"
              className="hidden"
              onChange={onPhotoChange}
              disabled={uploading}
              data-testid="profile-photo-input-camera"
            />
          </label>
          <label
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0B132B] text-white text-sm font-semibold cursor-pointer hover:bg-[#1a2a4d] transition-colors"
            data-testid="profile-photo-upload-label"
          >
            <Upload className="w-4 h-4" />
            {uploading
              ? "Uploading…"
              : hasPhoto
              ? "Change photo"
              : (
                <>
                  <span className="sm:hidden">Choose from library</span>
                  <span className="hidden sm:inline">Upload photo</span>
                </>
              )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={onPhotoChange}
              disabled={uploading}
              data-testid="profile-photo-input"
            />
          </label>
          {hasPhoto && (
            <button
              onClick={onPhotoRemove}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-semibold text-[#0B132B] hover:border-[#B00020] hover:text-[#B00020] transition-colors"
              data-testid="profile-photo-remove"
            >
              <Trash2 className="w-4 h-4" /> Remove
            </button>
          )}
          <p className="text-xs text-[#4B5563] self-center">
            JPEG, PNG or WebP &middot; up to 5MB
          </p>
        </div>

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-8 text-sm mt-8">
          <div>
            <dt className="text-xs uppercase tracking-wider text-[#4B5563] font-semibold mb-1">
              Email verified
            </dt>
            <dd className="text-[#0B132B] font-medium">
              {user.email_verified ? "Yes" : "Pending"}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-[#4B5563] font-semibold mb-1">
              Member since
            </dt>
            <dd className="text-[#0B132B] font-medium">
              {user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
            </dd>
          </div>
        </dl>
      </div>

      {/* Settings — deliberately small; not a standalone Settings system in Phase 1. */}
      <div className="bg-white rounded-2xl border border-gray-100 p-8" data-testid="profile-settings">
        <h2 className="text-lg font-bold text-[#0B132B] mb-2">Settings</h2>
        <p className="text-sm text-[#4B5563] mb-5">
          A small set of personal controls. More will move here as the portal grows.
        </p>

        <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-100 p-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-[#23803F] mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-[#0B132B]">
                Replay Northstar Introduction
              </p>
              <p className="text-xs text-[#4B5563] mt-0.5">
                Watch your awakening again. It won&rsquo;t change your Buddy
                connection, your journey status, or anything else.
              </p>
            </div>
          </div>
          <button
            onClick={onReplay}
            disabled={activation?.status !== "activated" || replaying}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-semibold text-[#0B132B] hover:border-[#2D9B4E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
            data-testid="profile-replay-awakening"
          >
            <RefreshCw className={`w-4 h-4 ${replaying ? "animate-spin" : ""}`} />
            {replaying ? "Opening…" : "Replay"}
          </button>
        </div>
        {activation && activation.status !== "activated" && (
          <p className="mt-3 text-xs text-[#4B5563]" data-testid="profile-replay-locked-hint">
            Available once you&rsquo;re connected with your BTC Buddy.
          </p>
        )}
      </div>

      <EmailPreferencesSection />

      <GeographicMatchingSection />
    </div>
  );
}

/**
 * EA-3 — Geographic matching (optional).
 *
 * Deliberate design choices:
 *   - Consent is explicit and revocable.  Revocation deletes the
 *     stored location on the server.
 *   - Location is administrative (country / state / county / postal)
 *     only — no GPS, no map, no live tracking.
 *   - The section is fully optional.  Patients who do not opt in see
 *     no location UI.
 *   - The card never surfaces geographic-eligibility "scores" — this
 *     is not a matching or ranking surface.
 */
function GeographicMatchingSection() {
  const [consent, setConsent] = useState(null);
  const [loc, setLoc] = useState({ country: "", state: "", county: "", postal_code: "" });
  const [busy, setBusy] = useState(false);

  const refresh = React.useCallback(async () => {
    try {
      const c = await api.get("/ea3/consent/me");
      setConsent(c.data?.state || "NEVER");
      if (c.data?.state === "GRANTED") {
        try {
          const l = await api.get("/ea3/location/me");
          setLoc({
            country: l.data?.country || "",
            state: l.data?.state || "",
            county: l.data?.county || "",
            postal_code: l.data?.postal_code || "",
          });
        } catch { /* empty */ }
      }
    } catch { setConsent("NEVER"); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const grant = async () => {
    setBusy(true);
    try {
      await api.post("/ea3/consent");
      toast.success("Geographic matching enabled.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not enable.");
    } finally { setBusy(false); }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      await api.post("/ea3/consent/withdraw");
      setLoc({ country: "", state: "", county: "", postal_code: "" });
      toast.success("Geographic matching disabled. Location cleared.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disable.");
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      ["country", "state", "county", "postal_code"].forEach((k) => {
        if ((loc[k] || "").trim()) payload[k] = loc[k].trim();
      });
      await api.put("/ea3/location/me", payload);
      toast.success("Location saved.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally { setBusy(false); }
  };

  const clearLoc = async () => {
    setBusy(true);
    try {
      await api.delete("/ea3/location/me");
      setLoc({ country: "", state: "", county: "", postal_code: "" });
      toast.success("Location cleared.");
    } catch (e) {
      toast.error("Could not clear.");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-8" data-testid="profile-ea3-section">
      <div className="flex items-start gap-3 mb-2">
        <MapPin className="w-5 h-5 text-[#1A5FAB] mt-0.5" />
        <div>
          <h2 className="text-lg font-bold text-[#0B132B]">
            Geographic matching (optional)
          </h2>
          <p className="text-sm text-[#4B5563] mt-0.5">
            Tell BTC roughly where you are so we can flag resources whose
            coverage areas apply. This never becomes a "best match" or
            "closest" ranking — it only marks whether a resource applies to
            your area at all. You can turn it off any time; disabling
            removes your stored location.
          </p>
        </div>
      </div>

      {consent !== "GRANTED" && (
        <div className="mt-4 flex items-center justify-between gap-4 rounded-xl border border-gray-100 p-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-[#23803F] mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-[#0B132B]">
                Turn on geographic matching
              </p>
              <p className="text-xs text-[#4B5563] mt-0.5">
                BTC never guesses your location from IP, GPS, or any other
                signal. You supply it explicitly.
              </p>
            </div>
          </div>
          <button
            data-testid="ea3-consent-grant"
            onClick={grant}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0B132B] text-white text-sm font-semibold hover:bg-[#1a2a4d] disabled:opacity-50 shrink-0"
          >Enable</button>
        </div>
      )}

      {consent === "GRANTED" && (
        <div data-testid="ea3-location-editor" className="mt-4 rounded-xl border border-gray-100 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase text-[#4B5563] font-semibold">Country</span>
              <input
                data-testid="ea3-input-country"
                value={loc.country}
                onChange={(e) => setLoc({ ...loc, country: e.target.value.toUpperCase() })}
                placeholder="US"
                maxLength={3}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase text-[#4B5563] font-semibold">State / Province</span>
              <input
                data-testid="ea3-input-state"
                value={loc.state}
                onChange={(e) => setLoc({ ...loc, state: e.target.value.toUpperCase() })}
                placeholder="CA"
                maxLength={8}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase text-[#4B5563] font-semibold">County / Admin area</span>
              <input
                data-testid="ea3-input-county"
                value={loc.county}
                onChange={(e) => setLoc({ ...loc, county: e.target.value })}
                placeholder="San Francisco"
                maxLength={64}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase text-[#4B5563] font-semibold">Postal code</span>
              <input
                data-testid="ea3-input-postal"
                value={loc.postal_code}
                onChange={(e) => setLoc({ ...loc, postal_code: e.target.value.toUpperCase() })}
                placeholder="94103"
                maxLength={16}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </label>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              data-testid="ea3-location-save"
              onClick={save}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0B132B] text-white text-sm font-semibold hover:bg-[#1a2a4d] disabled:opacity-50"
            >Save</button>
            <button
              data-testid="ea3-location-clear"
              onClick={clearLoc}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-semibold text-[#0B132B]"
            >Clear location</button>
            <button
              data-testid="ea3-consent-withdraw"
              onClick={withdraw}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-semibold text-[#B00020] hover:border-[#B00020]"
            >Disable & delete</button>
          </div>
          <p className="mt-3 text-xs text-[#4B5563]">
            Geographic matching does not imply BTC endorsement of any
            resource. Approval, availability, and provenance remain
            separate decisions.
          </p>
        </div>
      )}
    </div>
  );
}
